/**
 * Swiper для ленты курсов и обновление цен без перезагрузки.
 */
import Swiper from 'swiper';
import {
    Autoplay
} from 'swiper/modules';

class CryptocoinWidget {

    /**
     * Подключает Swiper и таймер обновления, если лента есть на странице.
     */
    constructor() {
        this.root = document.querySelector('.cryptocoin-widget');
        this.intervalMs = 2 * 60 * 1000;
        this.updatedAt = Date.now();
        this.loading = false;
        this.timer = 0;

        if (!this.root) {
            return;
        }

        this.mount();
    }

    /**
     * Создаёт Swiper для ленты курсов.
     */
    mount() {
        this.swiper = new Swiper(this.root, this.getDefaultConfig());

        if (this.root.dataset.endpoint) {
            this.bindRefresh();
        }
    }

    /**
     * Непрерывная прокрутка слайдов.
     */
    getDefaultConfig() {
        return {
            modules: [Autoplay],
            slidesPerView: 'auto',
            spaceBetween: 8,
            loop: true,
            speed: 6000,
            autoplay: {
                enabled: true,
                delay: 1,
                disableOnInteraction: false,
            },
        };
    }

    /**
     * Обновляет курсы по таймеру и при возврате на вкладку.
     */
    bindRefresh() {
        this.timer = window.setInterval(() => {
            this.refresh();
        }, this.intervalMs);

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.refresh();
            }
        });
    }

    /**
     * Запрашивает курсы, если с прошлого успешного ответа прошло достаточно времени.
     */
    refresh() {
        if (this.loading || document.hidden) {
            return;
        }

        if (Date.now() - this.updatedAt < this.intervalMs) {
            return;
        }

        this.loading = true;
        this.loadCoins()
            .then((coins) => {
                this.applyCoins(coins);
                this.updatedAt = Date.now();
            })
            .catch(() => {})
            .finally(() => {
                this.loading = false;
            });
    }

    /**
     * Скачивает свежие курсы с ajax-эндпоинта ленты.
     */
    loadCoins() {
        return fetch(this.root.dataset.endpoint, {
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
            },
        }).then((response) => {
            if (!response.ok) {
                throw new Error('cryptocoin');
            }

            return response.json();
        });
    }

    /**
     * Подставляет цену и изменение во все копии слайда, включая клоны Swiper.
     */
    applyCoins(coins) {
        if (!Array.isArray(coins)) {
            return;
        }

        coins.forEach((coin) => {
            this.applyCoin(coin);
        });
    }

    /**
     * Обновляет одну монету по data-id.
     */
    applyCoin(coin) {
        const id = coin && coin.id ? String(coin.id) : '';

        if (!id) {
            return;
        }

        const items = this.root.querySelectorAll(
            '.cryptocoin-widget__item[data-id="' + CSS.escape(id) + '"]'
        );

        items.forEach((item) => {
            this.writePrice(item, coin);
        });
    }

    /**
     * Записывает цену и направление изменения в слайд.
     */
    writePrice(item, coin) {
        const value = item.querySelector('.cryptocoin-widget__value');
        const change = item.querySelector('.cryptocoin-widget__change');
        const changeValue = item.querySelector('.cryptocoin-widget__change-value');
        const direction = Number(coin.change) >= 0 ? 'up' : 'down';

        if (value) {
            value.textContent = '$ ' + this.formatNumber(coin.price, this.priceDigits(coin.price));
        }

        if (changeValue) {
            changeValue.textContent = this.formatNumber(Math.abs(Number(coin.change) || 0), 2) + '%';
        }

        if (change) {
            change.classList.toggle('cryptocoin-widget__change--up', direction === 'up');
            change.classList.toggle('cryptocoin-widget__change--down', direction === 'down');
        }
    }

    /**
     * Выбирает число знаков после запятой так же, как шаблон PHP.
     */
    priceDigits(price) {
        const value = Math.abs(Number(price) || 0);

        if (value > 0 && value < 0.0001) {
            return 8;
        }

        if (value > 0 && value < 0.01) {
            return 6;
        }

        if (value > 0 && value < 1) {
            return 4;
        }

        return 2;
    }

    /**
     * Форматирует число как PHP number_format: запятая тысяч, точка дроби.
     */
    formatNumber(value, digits) {
        const parts = Math.abs(Number(value) || 0).toFixed(digits).split('.');

        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

        return parts.join('.');
    }
}

export default CryptocoinWidget;